import React, { useState, useMemo, useEffect, ChangeEvent } from 'react'
import {
  Button,
  Grid,
  CircularProgress,
  Box,
  FormHelperText,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import { Modal } from 'components/core-ui/Modal'
import { useNotification } from 'hooks'
import {
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useStatusAssignmentQuery,
  useExternalTypesQuery,
  useCountriesQuery,
  useManagersQuery,
  useDelegatesQuery,
  useStoresQuery,
  useLegalEntityTypesQuery
} from '../../../api'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { EditAssignmentModalProps } from '../../../interfaces'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import {
  Autocomplete,
  RadioButtonGroup,
  Checkbox as CustomCheckbox,
  Input,
  DatePicker,
  IconButton
} from '@fullscreendigital/carrefour-dex-fe'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema } from './helpers'
import { AutoCompleteValue } from 'components/core-ui/Table/TableDefinitions'
import { useSelectSearch } from 'hooks/useSelectSearch'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import dayjs from 'dayjs'
import { AddShoppingCart, GroupsOutlined, Person } from '@mui/icons-material'
import EditManagerModal from 'components/iam/partners/Managers/EditManager/EditManagerModal'
import EditDelegateModal from 'components/iam/partners/Delegates/EditDelegate/EditDelegateModal'
import { NewStoreModal } from 'components/iam/partners/Stores/NewStoreModal'
import { toAssignmentPayload } from './serializers'
import { makeCorePostRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { handleQueryError } from 'utils/helpers'

const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
  isModalOpen = false,
  onModalClose,
  assignmentId,
  partnerId,
  setShouldRefreshData
}) => {
  const [externalPartnerTypeOptions, setExternalPartnerTypeOptions] = useState<
    AutocompleteOption[]
  >([])
  const [isNewLegalEntity, setIsNewLegalEntity] = useState(false)
  const [countriesOptions, setCountriesOptions] = useState([])
  const [isLDAPdisabled, setIsLDAPDisabled] = useState(false)
  const [managersOptions, setManagersOptions] = useState([])
  const [delegatesOptions, setDelegatesOptions] = useState([])
  const [storesOptions, setStoresOptions] = useState([])
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false)
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false)
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false)
  const [typesOptions, setTypesOptions] = useState([])

  const openManagerModal = () => setIsManagerModalOpen(true)
  const closeManagerModal = () => setIsManagerModalOpen(false)

  const openDelegateModal = () => setIsDelegateModalOpen(true)
  const closeDelegateModal = () => setIsDelegateModalOpen(false)

  const openStoreModal = () => setIsStoreModalOpen(true)
  const closeStoreModal = () => setIsStoreModalOpen(false)

  const { showNotification } = useNotification()

  const { data: externalTypesData, isLoading: externalTypesLoading } = useExternalTypesQuery(
    showNotification,
    '/iam/external-partners/external-partner-types',
    { enabled: isModalOpen }
  )

  useEffect(() => {
    if (externalTypesData) {
      setExternalPartnerTypeOptions(
        externalTypesData.list
          ?.filter(s => !s.name.toLowerCase().endsWith('_old'))
          .map(s => ({
            label: s.name,
            value: s.id,
            extensionPeriod: s.extensionPeriod,
            group: s.group
          })) ?? []
      )
    }
  }, [externalTypesData])

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    refetch: refetchCountries
  } = useCountriesQuery(
    showNotification,
    toTableGETUrl({
      page: 0,
      rowsPerPage: 1000,
      url: '/iam/external-partners/countries'
    }),
    { enabled: isModalOpen }
  )

  useEffect(() => {
    if (countriesData) {
      setCountriesOptions(
        countriesData.map(country => ({
          label: country.name,
          value: country.id
        }))
      )
    }
  }, [countriesData])

  const {
    data: managersData,
    isLoading: isManagersLoading,
    refetch: refetchManagers
  } = useManagersQuery(
    showNotification,
    '/iam/external-partners/summaries/managers-slim',
    { enabled: isModalOpen }
  )

  useEffect(() => {
    if (managersData) {
      setManagersOptions(
        managersData.map(manager => ({
          label: manager.fullName,
          value: manager.id
        }))
      )
    }
  }, [managersData])

  const {
    data: delegatesData,
    isLoading: isDelegatesLoading,
    refetch: refetchDelegates
  } = useDelegatesQuery(
    showNotification,
    toTableGETUrl({
      url: '/iam/external-partners/summaries/delegates',
      tableFilters: [{ name: 'isEnabled', value: true }]
    }),
    { enabled: isModalOpen }
  )

  useEffect(() => {
    if (delegatesData) {
      setDelegatesOptions(
        delegatesData.map(delegate => ({
          label: `${delegate.firstName} ${delegate.lastName}`,
          value: delegate.id
        }))
      )
    }
  }, [delegatesData])

  const { data: statusData, isLoading: statusLoading } = useStatusAssignmentQuery(
    showNotification,
    '/iam/external-partners/statuses',
    { enabled: isModalOpen }
  )

  const updateAssignmentMutation = useUpdateAssignmentMutation(showNotification)
  const createAssignmentMutation = useCreateAssignmentMutation(showNotification)

  const initialValues = useMemo(() => getInitialValues(), [])

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const mutateAssignment = async values => {
    await makeCorePostRequest('/iam/external-partners/assignments', values)
  }

  const { isLoading, mutate } = useMutation(mutateAssignment, {
    onSuccess: res => {
      setShouldRefreshData(true)
      cancelModal()
      showNotification({
        message: 'Assignment saved successfully.',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        'There was an error saving the assignment. Please try again.',
        showNotification
      )
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(
        toAssignmentPayload(values, partnerId, statusData?.list.find(s => s.name === 'Active')?.id)
      )
    }
  })

  const {
    data: storesData,
    isLoading: isStoresLoading,
    refetch: refetchStores
  } = useStoresQuery(
    showNotification,
    toTableGETUrl({
      url: '/iam/external-partners/summaries/stores',
      tableFilters: [{ name: 'isEnabled', value: true }],
      ...(formik.values.legalEntityId
        ? {
            extraCustomUrlParams: [
              {
                name: 'legalEntityId',
                value: formik.values.legalEntityId
              }
            ]
          }
        : {})
    }),
    { enabled: isModalOpen && formik.values.externalPartnerType?.label === 'Mandatar' }
  )

  useEffect(() => {
    if (storesData) {
      setStoresOptions(
        storesData.map(store => ({
          label: store.storeName ?? '',
          value: store.id
        }))
      )
    }
  }, [storesData])

  const cancelModal = () => {
    onModalClose()
    formik.resetForm()
  }

  const {
    data: typesData,
    isLoading: isTypesLoading,
    refetch: refetchTypes
  } = useLegalEntityTypesQuery(showNotification, '/iam/external-partners/legal-entity-type', {
    enabled: isModalOpen
  })

  useEffect(() => {
    if (typesData) {
      setTypesOptions(
        typesData.map(type => ({
          label: type.name,
          value: type.id
        }))
      )
    }
  }, [typesData])

  const legalEntitySelectSearch = useSelectSearch<any, any>({
    url: toTableGETUrl({
      url: '/iam/external-partners/legal-entities',
      tableFilters: [{ name: 'isEnabled', value: true }]
    }),
    searchFields: ['name', 'code'],
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.id
    }),
    initialSearchText: isNewLegalEntity ? '' : (formik.values?.legalEntityName ?? '')
  })

  return (
    <>
      <EditManagerModal
        isModalOpen={isManagerModalOpen}
        onModalClose={closeManagerModal}
        onSuccess={() => refetchManagers()}
      />
      <EditDelegateModal
        isModalOpen={isDelegateModalOpen}
        onModalClose={closeDelegateModal}
        onSuccess={() => refetchDelegates()}
      />
      <NewStoreModal
        isModalOpen={isStoreModalOpen}
        closeModal={closeStoreModal}
        onSuccess={async store => {
          await refetchStores()
          formik.setFieldValue('store', [
            ...(formik.values.store || []),
            { label: store.name, value: store.id }
          ])
        }}
      />
      <Modal
        isModalOpen={isModalOpen}
        closeModal={onModalClose}
        modalTitle={assignmentId ? 'Edit Assignment' : 'Create Assignment'}
        modalWidth="50%"
      >
        <Grid container spacing={2}>
          {isLoading || createAssignmentMutation.isLoading || updateAssignmentMutation.isLoading ? (
            <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
          ) : (
            <>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={7}>
                    <AsyncAutocomplete
                      disableClearable
                      variant="outlined"
                      name="externalPartnerType"
                      label="Partner Type"
                      value={formik.values.externalPartnerType}
                      options={externalPartnerTypeOptions}
                      onChange={value => {
                        formik.setFieldValue('externalPartnerType', value)
                        formik.setFieldValue(
                          'extensionPeriod',
                          `${Math.round(value.extensionPeriod / 30)}`
                        )
                        formik.setFieldValue('store', [])
                      }}
                      required
                      error={!!formik.errors.externalPartnerType}
                      helperText={
                        ((formik.errors.externalPartnerType as unknown as AutoCompleteValue)
                          ?.value || formik.errors.externalPartnerType) as string
                      }
                    />
                  </Grid>
                  <Grid item xs={5} style={{ position: 'relative' }}>
                    <Box style={{ marginTop: '20px', position: 'absolute', right: '-14px' }}>
                      <RadioButtonGroup
                        label="Extension period (months)"
                        dotStyle
                        name="extensionPeriod"
                        onChange={(e, option) => formik.setFieldValue('extensionPeriod', option)}
                        value={formik.values.extensionPeriod}
                        defaultValue={
                          formik.values.externalPartnerType?.label === 'Mandatar'
                            ? formik.initialValues.extensionPeriod
                            : undefined
                        }
                        row
                        disabled={
                          !['Mandatar', 'System', 'Tester'].includes(
                            formik.values.externalPartnerType?.label
                          )
                        }
                        options={[
                          {
                            label: '1',
                            value: '1'
                          },
                          {
                            label: '3',
                            value: '3'
                          },
                          {
                            label: '6',
                            value: '6'
                          },
                          {
                            label: '12',
                            value: '12'
                          }
                        ]}
                      />
                      {formik.errors.extensionPeriod && (
                        <FormHelperText error>{formik.errors.extensionPeriod}</FormHelperText>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={8}>
                <Autocomplete
                  {...legalEntitySelectSearch}
                  label="Select legal entity"
                  variant="outlined"
                  freeSolo
                  autoComplete={false}
                  filterOptions={x => x}
                  value={formik.values.existingLegalEntityName}
                  onChange={(event, value) => {
                    if (value?.name) {
                      formik.setFieldValue('legalEntityId', value.id)
                      formik.setFieldValue('existingLegalEntityName', value.name)
                      formik.setFieldValue('legalEntityName', value.name)
                      formik.setFieldValue('legalEntityCIF', value.code)
                      formik.setFieldValue('legalEntityCountry', {
                        label: value.country.name,
                        value: value.country.id
                      })
                    } else {
                      formik.setFieldValue('legalEntityId', '')
                      formik.setFieldValue('existingLegalEntityName', value)
                      formik.setFieldValue('legalEntityName', '')
                      formik.setFieldValue('legalEntityCIF', '')
                      formik.setFieldValue('legalEntityCountry', '')
                    }
                  }}
                  onClose={event => {
                    if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
                      formik.setFieldValue(
                        'existingLegalEntityName',
                        (event.target as HTMLInputElement).value
                      )
                      formik.setFieldValue('legalEntityName', '')
                      formik.setFieldValue('legalEntityId', '')
                      formik.setFieldValue('legalEntityCIF', '')
                      formik.setFieldValue('legalEntityCountry', '')
                    }
                  }}
                  error={!!formik.errors.existingLegalEntityName}
                  helperText={formik.errors.existingLegalEntityName as string}
                  disabled={isNewLegalEntity}
                />
              </Grid>
              <Grid
                item
                xs={4}
                style={{ display: 'flex', alignItems: 'center', lineHeight: '56px' }}
              >
                <Box style={{ position: 'relative', left: '16px', top: '6px' }}>
                  <CustomCheckbox
                    checked={isNewLegalEntity}
                    label="New legal entity"
                    onChange={() => {
                      setIsNewLegalEntity(!isNewLegalEntity)
                      legalEntitySelectSearch.setSearchText('')
                      formik.setFieldValue('legalEntityId', null)
                      formik.setFieldValue('existingLegalEntityName', null)
                      formik.setFieldValue('legalEntityName', '')
                      formik.setFieldValue('legalEntityCIF', '')
                      formik.setFieldValue('legalEntityCountry', '')
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Input
                  type="text"
                  variant="outlined"
                  name="legalEntityName"
                  label="Legal entity name (ANAF registered name for Romanian companies)"
                  width="100%"
                  value={formik.values.legalEntityName}
                  onChange={formik.handleChange}
                  required
                  disabled={formik.values.legalEntityId || !isNewLegalEntity}
                  error={!!formik.errors.legalEntityName}
                  helperText={formik.errors.legalEntityName as string}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Input
                      type="text"
                      variant="outlined"
                      name="legalEntityCIF"
                      label="Fiscal identification code (CIF)"
                      width="100%"
                      value={formik.values.legalEntityCIF}
                      onChange={formik.handleChange}
                      required
                      disabled={formik.values.legalEntityId || !isNewLegalEntity}
                      error={!!formik.errors.legalEntityCIF}
                      helperText={formik.errors.legalEntityCIF as string}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AsyncAutocomplete
                      variant="outlined"
                      name="legalEntityCountry"
                      label="Legal entity country"
                      value={formik.values.legalEntityCountry}
                      options={countriesOptions}
                      onChange={value => formik.setFieldValue('legalEntityCountry', value)}
                      required
                      disabled={formik.values.legalEntityId || !isNewLegalEntity}
                      error={!!formik.errors.legalEntityCountry}
                      helperText={
                        typeof formik.errors.legalEntityCountry === 'object' &&
                        !Array.isArray(formik.errors.legalEntityCountry)
                          ? (formik.errors.legalEntityCountry?.value as string)
                          : ''
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Input
                  type="text"
                  variant="outlined"
                  name="jobTitle"
                  label="Job Title"
                  width="100%"
                  value={formik.values.jobTitle}
                  onChange={formik.handleChange}
                  required
                  error={!!formik.errors.jobTitle}
                  helperText={formik.errors.jobTitle as string}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      variant="outlined"
                      label="Start Date"
                      value={dayjs(formik.values.startDate)}
                      onChange={value => formik.setFieldValue('startDate', value)}
                      error={!!formik.errors.startDate}
                      helperText={formik.errors.startDate as string}
                      required
                      style={{ width: '100%' }}
                      minDate={dayjs()}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      variant="outlined"
                      label="End Date"
                      value={dayjs(formik.values.endDate)}
                      onChange={value => formik.setFieldValue('endDate', value)}
                      error={!!formik.errors.endDate}
                      helperText={formik.errors.endDate as string}
                      minDate={dayjs(formik.values.startDate)}
                      style={{ width: '100%' }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.adAccount}
                        name="adAccount"
                        onChange={formik.handleChange}
                      />
                    }
                    label="AD Account"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.ldapAccount}
                        name="ldapAccount"
                        onChange={formik.handleChange}
                      />
                    }
                    label="LDAP Account"
                    disabled={isLDAPdisabled}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.googleAccount}
                        name="googleAccount"
                        onChange={event => {
                          const isChecked = (event.target as HTMLInputElement).checked
                          formik.setFieldValue('googleAccount', isChecked)

                          if (isChecked) {
                            setIsLDAPDisabled(true)
                            formik.setFieldValue('ldapAccount', true)
                          } else {
                            setIsLDAPDisabled(false)
                            formik.setFieldValue('ldapAccount', false)
                          }
                        }}
                      />
                    }
                    label="Google Account"
                  />
                </FormGroup>
                {formik.touched.googleAccount && formik.errors.googleAccount ? (
                  <FormHelperText error>{formik.errors.googleAccount}</FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <AsyncAutocomplete
                      variant="outlined"
                      name="status"
                      label="Manager"
                      value={formik.values.manager}
                      options={managersOptions}
                      onChange={value => {
                        formik.setFieldValue('manager', value)
                        if (value.delegate) {
                          formik.setFieldValue('delegate', value.delegate)
                        } else {
                          formik.setFieldValue('delegate', null)
                        }
                      }}
                      required
                      error={!!formik.errors.manager}
                      helperText={formik.errors.manager as string}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                      <IconButton
                        endIcon={<Person />}
                        variant="outlined"
                        label="New manager"
                        onClick={() => openManagerModal()}
                        style={{ flex: 'none' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <AsyncAutocomplete
                      variant="outlined"
                      name="status"
                      label="Delegate"
                      value={formik.values.delegate}
                      options={delegatesOptions}
                      onChange={value => formik.setFieldValue('delegate', value)}
                      error={!!formik.errors.delegate}
                      helperText={formik.errors.delegate as string}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                      <IconButton
                        endIcon={<GroupsOutlined />}
                        variant="outlined"
                        label="New delegate"
                        onClick={() => openDelegateModal()}
                        style={{ flex: 'none' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              {formik.values.externalPartnerType?.label === 'Mandatar' && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <AsyncAutocomplete
                        variant="outlined"
                        name="store"
                        label="Store"
                        value={formik.values.store}
                        options={storesOptions}
                        multiple
                        required
                        onChange={value => formik.setFieldValue('store', value)}
                        error={!!formik.errors.store}
                        helperText={formik.errors.store as string}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                        <IconButton
                          endIcon={<AddShoppingCart />}
                          variant="outlined"
                          label="Assign new store"
                          onClick={() => openStoreModal()}
                          style={{ flex: 'none' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Input
                  type="textarea"
                  name="assignmentDetails"
                  label="Additional details"
                  style={{ width: '100%' }}
                  rows={3}
                  variant="outlined"
                  value={formik.values.assignmentDetails}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue('assignmentDetails', event.currentTarget.value)
                  }
                  error={!!formik.errors.assignmentDetails}
                  helperText={formik.errors.assignmentDetails as string}
                />
              </Grid>
              <Grid item xs={12} className={s.modalButtons}>
                <Button variant="outlined" onClick={onModalClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => formik.handleSubmit()}
                  disabled={isLoading}
                >
                  Save
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Modal>
    </>
  )
}

export default EditAssignmentModal
